import * as Sentry from '@sentry/react';
import { isBridgeMissingInElectron, isWeb } from '~/utils/helper/device';

const SENTRY_DSN =
  'https://c0986b39c705a95875a2b61c1bd88dc8@o4510033186127873.ingest.us.sentry.io/4511585417625600';

const IGNORE_ERRORS: (string | RegExp)[] = [
  /Cannot read properties of null \(reading 'sequence'\)/,
  'Non-Error promise rejection captured with value: undefined',
];

const BRIDGE_MISSING_REPORTED_KEY = 'nbx3-951-bridge-missing-reported';

/**
 * UA 說是桌機版、桌機橋接卻不存在，代表頁面被開在一個沒有 preload 的 Electron
 * 視窗裡（南一會員中心首頁產品格的 window.open）。功能分流已改成能力偵測、
 * 不會再崩潰，但這個視窗本身仍是壞的，要能看見還有多少人走到這裡。
 *
 * 同一個分頁只回報一次：老師看到異常時會反覆重新整理，不節流會由少數人洗掉額度。
 */
const reportBridgeMissing = () => {
  if (!isBridgeMissingInElectron) return;

  try {
    if (sessionStorage.getItem(BRIDGE_MISSING_REPORTED_KEY)) return;
    sessionStorage.setItem(BRIDGE_MISSING_REPORTED_KEY, '1');
  } catch {
    // sessionStorage 不可用時仍然回報，頻率退回「每次頁面載入一次」
  }

  Sentry.captureMessage('desktop bridge missing in electron shell', {
    level: 'warning',
    tags: { issue: 'NBX3-951' },
    extra: {
      href: window.location.href,
      referrer: document.referrer,
      userAgent: navigator.userAgent,
      // 只有被 window.open 開出來的視窗才有 opener，用來確認入口就是產品格
      openedByWindowOpen: window.opener !== null,
      isTopLevel: window.top === window.self,
    },
  });
};

const isRtkQueryError = (value: unknown): boolean =>
  typeof value === 'object' &&
  value !== null &&
  'status' in value &&
  ('data' in value || 'error' in value);

export const initSentry = () => {
  // 本機備援頁必須能在完全斷網時運作，也不應嘗試送出監控請求。
  if (window.__NANIBOX_OFFLINE__) return;

  const environment = import.meta.env.VITE_ENV || 'release';

  Sentry.init({
    dsn: SENTRY_DSN,
    environment,
    sendDefaultPii: true,
    // 第三方追蹤 script（Microsoft Clarity）內部錯誤不上報，避免浪費額度
    denyUrls: [/clarity\.ms/],
    ignoreErrors: IGNORE_ERRORS,
    beforeSend(event, hint) {
      if (isRtkQueryError(hint?.originalException)) {
        return null;
      }
      return event;
    },
    initialScope: {
      tags: {
        platform: isWeb ? 'web' : 'electron',
        // 讓這種視窗發出的每一筆事件都可以在 Sentry 上被篩出來，不只這支回報
        ...(isBridgeMissingInElectron ? { shell: 'electron-no-bridge' } : {}),
      },
    },
  });

  reportBridgeMissing();
};

export const setSentryUser = (user: {
  id: string;
  username?: string;
  authority?: string;
}) => {
  Sentry.setUser({
    id: user.id,
    username: user.username,
  });
  if (user.authority) {
    Sentry.setTag('authority', user.authority);
  }
};

export const clearSentryUser = () => {
  Sentry.setUser(null);
};
